import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiBell, FiMail, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { ProfileModal } from '../profile/ProfileModal';
import { BrandLogo } from './BrandLogo';
import supabase from '../../supabase/supabase';

export const Header = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const displayName = profile?.full_name || 'System User';
  const displayRole = profile?.company_name ||
    (profile?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Salvager Partner');
  const defaultAvatar = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80';
  const avatarUrl = profile?.avatar_url || defaultAvatar;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setIsNotifOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!profile?.id) return undefined;

    const fetchNotifications = async () => {
      const { data } = await supabase
        .from('notifications_1783875000000')
        .select('*')
        .or(`target_user_id.eq.${profile.id},target_role.eq.${profile.role}`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) setNotifications(data);
    };

    fetchNotifications();

    const channel = supabase
      .channel('notifications-channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications_1783875000000',
          filter: `target_user_id=eq.${profile.id}`
        },
        (payload) => setNotifications((prev) => [payload.new, ...prev])
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications_1783875000000',
          filter: `target_role=eq.${profile.role}`
        },
        (payload) => setNotifications((prev) => [payload.new, ...prev])
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, profile?.role]);

  const unreadCount = notifications.filter((notification) => !notification.is_read).length;

  const toggleNotifications = async () => {
    setIsNotifOpen(!isNotifOpen);

    if (!isNotifOpen && unreadCount > 0) {
      const unreadIds = notifications
        .filter((notification) => !notification.is_read)
        .map((notification) => notification.id);

      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, is_read: true }))
      );

      await supabase
        .from('notifications_1783875000000')
        .update({ is_read: true })
        .in('id', unreadIds);
    }
  };

  return (
    <>
      <header className="h-20 bg-surface-alt flex items-center justify-between px-8">
        <div className="flex-1" />

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex h-11 w-44 items-center justify-end border-r border-gray-200 pr-5">
            <BrandLogo className="h-9 w-full" />
          </div>

          <button className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-500 shadow-sm hover:text-brand transition-colors">
            <SafeIcon icon={FiMail} />
          </button>

          <div className="relative" ref={notifRef}>
            <button
              onClick={toggleNotifications}
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-500 shadow-sm hover:text-brand transition-colors relative"
            >
              <SafeIcon icon={FiBell} />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse" />
              )}
            </button>

            <AnimatePresence>
              {isNotifOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-14 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
                    <span className="text-xs text-gray-500">{notifications.length} recent</span>
                  </div>

                  <div className="max-h-[350px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-gray-400 text-sm">
                        No notifications yet.
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => {
                              setIsNotifOpen(false);
                              if (notification.link) navigate(notification.link);
                            }}
                            className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                              !notification.is_read ? 'bg-brand/5' : ''
                            }`}
                          >
                            <h4 className={`text-sm ${
                              !notification.is_read
                                ? 'font-bold text-gray-900'
                                : 'font-medium text-gray-700'
                            }`}>
                              {notification.title}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <span className="text-[10px] text-gray-400 mt-2 block">
                              {new Date(notification.created_at).toLocaleString([], {
                                dateStyle: 'short',
                                timeStyle: 'short'
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div
            className="flex items-center gap-3 pl-4 border-l border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setIsProfileModalOpen(true)}
          >
            <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm overflow-hidden bg-gray-100 flex items-center justify-center">
              {profile?.avatar_url ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = defaultAvatar;
                  }}
                  className="w-full h-full object-cover"
                />
              ) : (
                <SafeIcon icon={FiUser} className="text-gray-400" size={20} />
              )}
            </div>
            <div className="hidden md:block text-sm">
              <p className="font-medium text-gray-800 truncate max-w-[150px]">{displayName}</p>
              <p className="text-brand font-bold text-xs truncate max-w-[150px]">{displayRole}</p>
            </div>
          </div>
        </div>
      </header>

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </>
  );
};